import React, { useState, useRef, useEffect } from 'react';
import { fetchAnnouncements, fetchAssignments, fetchDiscussions, fetchFiles, fetchModules, fetchPages, fetchEnrollments, fetchQuizzes, fetchSyllabus } from '../../services/api/courseService';
import { triggerPiepeline, PipelineRun } from '../../services/api/virtualAssistant';
import { cancelPipeline } from '../../services/api/virtualAssistant';
import { CourseData, ExcludedItems } from "../../types/CourseData";
import { Pipeline } from "../../types/pipelineData";
import { getSafeImageSrc } from '../../utils/imageUtils';
import CanvasHeader from './CanvasHeader';
import CoursesSidebar from './CoursesSidebar';
import CourseTabNavigation from './CourseTabNavigation';
import ConnectTab from './ConnectTab';
import PreviewTab from './PreviewTab';
import CanvasFooter from './CanvasFooter';
import CanvasModals from './CanvasModals';

type VisibilityKey = 'announcements' | 'assignments' | 'discussions' | 'files' | 'modules' | 'pages' | 'enrollments' | 'quizzes' | 'syllabus';

interface CanvasCourseProps {
  isCanvasCourseModalOpen: boolean;
  setCanvasCourseModalOpen: (isOpen: boolean) => void;
  courses: CourseData[];
  setCourses: React.Dispatch<React.SetStateAction<CourseData[]>>;
  updateExcludedItems: (courseId: number, type: keyof ExcludedItems, itemId: string) => void;
  handleToggleChange: (key: VisibilityKey, courseId: number) => void;
  assistant: any;
  triggerAutoSave: () => void;
  isCanvasRNightly: boolean;
  setIsCanvasRNightly: React.Dispatch<React.SetStateAction<boolean>>;
  isCanvasFullLoad: boolean;
  setIsCanvasFullLoad: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ConnectionResponse {
  message: string;
  data: any;
  connection_status: 'success' | 'failure';
  tenant: string;
}

interface User {
  id: string;
  name: string;
  sis_user_id: string;
}

interface CourseItem {
  id: string;
  title: string;
  display_name: string;
  page_title: string;
  name: string;
  page_id: string;
  user: User;
  syllabus?: string;
  file_id?: string;
  module_id?: string;
  course_code?: string;
  isChecked: boolean;
  posted_at: Date;
  published: boolean;
  due_at: Date;
  will_become_available_on: Date;
}

const CanvasCourse: React.FC<CanvasCourseProps> = ({
  isCanvasCourseModalOpen,
  setCanvasCourseModalOpen,
  courses,
  setCourses,
  updateExcludedItems,
  handleToggleChange,
  assistant,
  triggerAutoSave,
  isCanvasRNightly,
  setIsCanvasRNightly,
  isCanvasFullLoad,
  setIsCanvasFullLoad
}) => {
  // Core state  
  const [selectedCourse, setSelectedCourse] = useState<number>(1);
  const [systemConfig, setSystemConfig] = useState<{ Id: string; ConfigValue: string; }[]>([]);
  const [pipelineData, setPipelineData] = useState<Pipeline[]>([]);
  const [gettingData, setGettingData] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<null | {
    progress: number;
    status: string;
    error?: string;
  }>(null);
  const [isGettingDataCalled, setIsGetDataCallled] = useState(true);
  const [getDataClickTime, setGetDataClickTime] = useState<Date | null>(null);
  const [pendingSave, setPendingSave] = useState(false);
  const [connectedCourseIds, setConnectedCourseIds] = useState<string[]>([]);
  const [connectUsing, setConnectUsing] = useState<string>('loop');
  const [lastSaveTarget, setLastSaveTarget] = useState<'azure' | 'azure'>('azure');
  const [safeAiIconSrc, setSafeAiIconSrc] = useState<string>(assistant?.app_ai_face);
  const [showDocumentCache, setShowDocumentCache] = useState(false);
  const [isLoadingTabs, setIsLoadingTabs] = useState<{ [courseId: number]: { [key: string]: boolean } }>({});
  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedGetData, setExpandedGetData] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [cancellingPipeline, setCancellingPipeline] = useState(false);
  // Modal states
  const [modalRefreshNModal, setModalRefreshNModal] = useState(false);
  const [showHowImage, setShowHowImage] = useState(false);
  const [modalSize, setModalSize] = useState('max-w-[40rem]');
  const [imageSrc, setImageSrc] = useState("");
  const [customModal, setCustomModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "loading" | "confirmation" | "warning";
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "confirmation",
  });

  // Refs
  const fetchCalledRef = useRef<{ [key: number]: boolean }>({});
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const expandedSectionRef = useRef<HTMLDivElement | null>(null);

  const CACHE_DURATION = 2 * 24 * 60 * 60 * 1000;

  // Utility functions
  const getCachedData = (key: string) => {
    const item = sessionStorage.getItem(key);
    if (!item) return null;
    const { value, timestamp } = JSON.parse(item);
    if (Date.now() - timestamp > CACHE_DURATION) {
      sessionStorage.removeItem(key);
      return null;
    }
    return value;
  };

  const showConfirmationModal = (message: string, title: string, onConfirm: () => void) => {
    setCustomModal({
      isOpen: true,
      title: title,
      message,
      type: "confirmation",
      onConfirm,
    });
  };

  const showDeleteConfirmationModal = (message: string, onConfirm: () => void) => {
    setCustomModal({
      isOpen: true,
      title: "Delete Confirmation",
      message,
      type: "confirmation",
      onConfirm,
    });
  };

  // Effects
  useEffect(() => {
    const updateSafeSource = async (): Promise<void> => {
      const src = assistant?.app_ai_face;
      const safeSrc = await getSafeImageSrc(src);
      setSafeAiIconSrc(safeSrc);
    };
    updateSafeSource();
  }, [assistant?.app_ai_face]);

  useEffect(() => {
    const config = getCachedData('systemConfig');
    if (config) {
      setSystemConfig(config);
    }
  }, []);

  useEffect(() => {
    const configValue = systemConfig.find(item => item.Id === 'document_cache_enabled_canvas')?.ConfigValue;
    const defaultDocumentCache = Boolean(configValue);
    setShowDocumentCache(defaultDocumentCache);
  }, [systemConfig]);

  // Pipeline management
// MODIFY fetchPipelineData function (around line 120)
const fetchPipelineData = async () => {
  const pipeline_name = "canvas_to_vectordb";
  if (assistant.app_id) {
    const resPipelineRun = await PipelineRun(pipeline_name, assistant.app_id);

    // ALWAYS store ALL pipelines for history display
    setPipelineData(resPipelineRun);

    // Filter: Only consider pipelines created AFTER user clicked "Get Data Now" for STATUS determination
    let relevantPipeline = resPipelineRun;
    if (getDataClickTime && resPipelineRun.length > 0) {
      relevantPipeline = resPipelineRun.filter((p: any) => {
        const pipelineCreatedAt = new Date(p.created_ts);
        return pipelineCreatedAt >= getDataClickTime;
      });

      // If no new pipelines found yet, keep polling but don't show old status
      if (relevantPipeline.length === 0 && gettingData) {
        setCurrentStatus({ progress: 20, status: "In Progress" });
        setIsGetDataCallled(true);
        return;  // Early return - pipelineData still has all history
      }
    }

    // Use relevantPipeline ONLY for status determination
    if (relevantPipeline && relevantPipeline.length > 0) {
      setIsGetDataCallled(true);

      const backendStatus = relevantPipeline[0].status;
      let status = '';

      // Map backend status to UI status - ALWAYS check actual status first
      if (backendStatus === 'STARTED') status = 'In Progress';
      if (backendStatus === 'failed') status = 'Failed';
      if (backendStatus === 'success') status = 'Complete';
      if (backendStatus === 'cancelled') {
        status = 'Cancelled';
        setCancellingPipeline(false); // Reset cancelling state
        setGettingData(false);
      }
      if (backendStatus === 'cancelling') {
        status = 'Cancelling';
        setCancellingPipeline(true);
      }
      if (backendStatus === 'cancel_failed') {
        status = 'Failed';
        setCancellingPipeline(false);
      }

      setCurrentStatus({ progress: 20, status: status });
    } else {
      if (gettingData) {
        setCurrentStatus({ progress: 20, status: "In Progress" });
        setIsGetDataCallled(true);
      } else {
        setCurrentStatus({ progress: 20, status: "" });
        setIsGetDataCallled(false);
      }
    }
  }
};

  useEffect(() => {
  fetchPipelineData();
  if (intervalRef.current) {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }
  // UPDATE CONDITION TO INCLUDE CANCEL STATES
  if (currentStatus?.status !== 'Complete' && currentStatus?.status !== 'Cancelled') {
    if (isGettingDataCalled) {
      intervalRef.current = setInterval(() => {
        fetchPipelineData();
      }, 10 * 1000);
    }
  }
  return () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };
}, [currentStatus?.status, isGettingDataCalled]);

  // Course item management
  const updateCourseItems = (itemData: any[], key: string, excluded: ExcludedItems, excludedKey: keyof ExcludedItems, idKey: keyof CourseItem) => {
    if (!Array.isArray(itemData)) {
      console.error(`Expected an array for updateCourseItems ${key}, but got:`, itemData);
      return [];
    }
    return itemData.map((item: any) => ({
      ...item,
      checked: !excluded[excludedKey].includes(item[idKey]),
    }));
  };

  const fetchAndUpdateItem = async (
    fetchFunc: Function,
    key: string,
    excludedKey: keyof ExcludedItems,
    idKey: keyof CourseItem,
    courseId: number,
    course_id: string,
    course_url: string,
    excluded: ExcludedItems,
    isRefresh?: boolean
  ) => {
    const app_id = assistant.app_id || '';
    const data = await fetchFunc(course_id, course_url, app_id, isRefresh).catch(() => ({ data: [] }));
    if (!data) {
      console.error(`Failed to fetch ${key}`);
      setIsLoadingTabs(prev => ({
        ...prev,
        [courseId]: { ...prev[courseId], [key]: false }
      }));
      return;
    }

    const updatedItems = updateCourseItems(data, key, excluded, excludedKey, idKey);

    setCourses(prev => prev.map(c =>
      c.id === courseId ? { ...c, courseItems: { ...c.courseItems, [key]: updatedItems } } : c
    ));

    setCheckedItems(prevChecked => {
      const updatedCheckedItems = updatedItems
        .filter(item => item.checked)
        .map(item => item[idKey]?.toString())
        .filter((item): item is string => item !== undefined);

      const allCheckedItems = [
        ...prevChecked,
        ...updatedCheckedItems.filter(item => !prevChecked.includes(item))
      ];

      return allCheckedItems;
    });

    setIsLoadingTabs(prev => ({
      ...prev,
      [courseId]: { ...prev[courseId], [key]: false }
    }));
  };

  const fetchCourseItems = async (courseId: number, isRefresh?: boolean) => {
    const course = courses.find(c => c.id === courseId);
    const course_id = course?.course_id;
    const course_url = course?.url || '';
    if (!course_id || !course || course.connectionState !== 'connected') return;

    setCourses(prev => prev.map(c =>
      c.id === courseId ? { ...c, isLoadingItems: true } : c
    ));

    setIsLoadingTabs(prev => ({
      ...prev,
      [courseId]: {
        announcements: true,
        assignments: true,
        discussions: true,
        files: true,
        modules: true,
        pages: true,
        enrollments: true,
        quizzes: true,
        syllabus: true,
      }
    }));

    try {
      const excluded = course.excludedItems;
      await Promise.all([
        fetchAndUpdateItem(fetchAnnouncements, 'announcements', 'excluded_announcements', 'id', courseId, course_id, course_url, excluded, isRefresh),
        fetchAndUpdateItem(fetchAssignments, 'assignments', 'excluded_assignments', 'id', courseId, course_id, course_url, excluded, isRefresh),
        fetchAndUpdateItem(fetchDiscussions, 'discussions', 'excluded_discussions', 'id', courseId, course_id, course_url, excluded, isRefresh),
        fetchAndUpdateItem(fetchFiles, 'files', 'excluded_files', 'file_id', courseId, course_id, course_url, excluded, isRefresh),
        fetchAndUpdateItem(fetchModules, 'modules', 'excluded_modules', 'module_id', courseId, course_id, course_url, excluded, isRefresh),
        fetchAndUpdateItem(fetchPages, 'pages', 'excluded_pages', 'page_id', courseId, course_id, course_url, excluded, isRefresh),
        fetchAndUpdateItem(fetchEnrollments, 'enrollments', 'excluded_enrollments', 'id', courseId, course_id, course_url, excluded, isRefresh),
        fetchAndUpdateItem(fetchQuizzes, 'quizzes', 'excluded_quizzes', 'id', courseId, course_id, course_url, excluded, isRefresh),
        fetchAndUpdateItem(fetchSyllabus, 'syllabus', 'excluded_syllabus', 'module_id', courseId, course_id, course_url, excluded, isRefresh),
      ]);
    } catch (error) {
      console.error('Failed to fetch course items:', error);
    }
  };

  // Course management
  useEffect(() => {
    const courseid_prefix = systemConfig.find(item => item.Id === 'canvas_courseid_prefix')?.ConfigValue;
    if (courses.length === 0) {
      addNewCourse();
    }
    if (courses.length > 0) {
      courses.forEach((course) => {
        if (!course.course_id && !course.connectionState) {
          setCourses(prev => prev.map(c =>
            c.id === course.id ? { ...c, connectionState: 'idle', activeTab: 'CONNECT' } : c
          ));
        }
        if (course.course_id && course.connectionState !== 'connected') {
          setCourses(prev => prev.map(c =>
            c.id === course.id ? { ...c, connectionState: 'connected', activeTab: 'CONNECT' } : c
          ));
        }
        if (course.connectionState === 'connected' && !fetchCalledRef.current[course.id]) {
          let courseUrlSegment = course.url.split('/').filter(Boolean).pop();
          if (courseUrlSegment) {
            if (courseUrlSegment?.length < 7) {
              courseUrlSegment = courseid_prefix + courseUrlSegment;
            }
            const finalSegment = courseUrlSegment;
            setConnectedCourseIds((prev) => [...prev, finalSegment]);
          }
          if (connectUsing === 'click') triggerAutoSave();
          fetchCourseItems(course.id);
          fetchCalledRef.current[course.id] = true;
        }
      });
    }
  }, [courses]);

  const addNewCourse = () => {
    const newOrder = courses.length + 1;
    const nextId = Math.max(...courses.map(course => course.id), 0) + 1;
    setCourses(prev => [...prev, {
      id: nextId,
      course_id: '',
      url: 'https://canvas.northwestern.edu/courses/',
      tenant: '',
      order: newOrder,
      course_name: '',
      activeTab: 'CONNECT',
      connectionState: 'idle',
      selectedItems: {
        announcements: false,
        assignments: false,
        discussions: false,
        files: false,
        modules: false,
        pages: false,
        enrollments: false,
        groups: false,
        quizzes: false,
        syllabus: false
      },
      excludedItems: {
        excluded_announcements: [],
        excluded_assignments: [],
        excluded_discussions: [],
        excluded_files: [],
        excluded_modules: [],
        excluded_pages: [],
        excluded_enrollments: [],
        excluded_quizzes: [],
        excluded_syllabus: [],
      },
      visibility: {
        announcements: true,
        assignments: true,
        discussions: true,
        files: true,
        modules: true,
        pages: true,
        enrollments: true,
        quizzes: true,
        syllabus: true,
      },
    }]);
    setSelectedCourse(nextId);
  };

  // Connection management
  const handleConnect = async (courseId: number, source: 'click' | 'loop') => {
    if (source === 'click') {
      setConnectUsing('click');
    }
    const courseid_prefix = systemConfig.find(item => item.Id === 'canvas_courseid_prefix')?.ConfigValue;
    const course = courses.find(c => c.id === courseId);

    if (!course?.url) {
      setCourses((prev) =>
        prev.map((c) =>
          c.id === courseId
            ? { ...c, UrlError: 'Please enter a valid URL.' }
            : c
        )
      );
      return;
    }

    const isCourseAlreadyConnected = courses.some(c => c.url.trim() === course.url.trim() && c.course_id !== "");
    if (isCourseAlreadyConnected) {
      setCourses((prev) =>
        prev.map((c) =>
          c.id === courseId
            ? { ...c, UrlError: 'This course is already connected.' }
            : c
        )
      );
      return;
    }

    let courseUrlSegment = course.url.trim().split('/').filter(Boolean).pop();
    if (courseUrlSegment) {
      if (courseUrlSegment?.length < 7) {
        courseUrlSegment = courseid_prefix + courseUrlSegment;
      }
      const finalSegment = courseUrlSegment;
      if (connectedCourseIds.includes(finalSegment)) {
        setCourses((prev) =>
          prev.map((c) =>
            c.id === courseId
              ? { ...c, UrlError: 'This course is already connected.', connectionState: 'idle' }
              : c
          )
        );
        return;
      }
      setConnectedCourseIds((prev) => [...prev, finalSegment]);
    }

    setCourses((prev) =>
      prev.map((c) =>
        c.id === courseId ? { ...c, UrlError: undefined } : c
      )
    );

    setCourses(prev => prev.map(course =>
      course.id === courseId
        ? { ...course, connectionState: 'connecting' }
        : course
    ));

    try {
      const canvasUser = systemConfig.find(item => item.Id === 'canvas_system_user')?.ConfigValue;
      if (!canvasUser) {
        throw new Error('Canvas system user not found in configuration');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/canvas/create-connection`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          course_url: course.url,
          canvas_user: canvasUser
        })
      });

      const result: ConnectionResponse = await response.json();

      if (result.connection_status === 'success') {
        setCourses(prev => prev.map(c =>
          c.id === courseId
            ? { ...c, connectionState: 'connected', course_id: result.data.id, course_name: result.data.name, tenant: result?.tenant }
            : c
        ));

        setTimeout(() => {
          setActiveTab(courseId, 'PREVIEW');
          if (source === 'click') setPendingSave(true);
          else if (!isGettingDataCalled) setPendingSave(true);
        }, 2000);
      } else {
        const courseToDelete = courses.find(c => c.id === courseId);
        const urls = courseToDelete?.url;
        if (urls) {
          let courseUrlSegment = urls.trim().split('/').filter(Boolean).pop();
          if (courseUrlSegment) {
            if (courseUrlSegment?.length < 7) {
              courseUrlSegment = courseid_prefix + courseUrlSegment;
            }
            const finalSegment = courseUrlSegment;
            if (finalSegment) {
              setConnectedCourseIds(prev =>
                prev.filter(id => id !== String(finalSegment))
              );
            }
          }
        }
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Connection failed:', error);
      const courseToDelete = courses.find(c => c.id === courseId);
      const urls = courseToDelete?.url;
      if (urls) {
        let courseUrlSegment = urls.trim().split('/').filter(Boolean).pop();
        if (courseUrlSegment) {
          if (courseUrlSegment?.length < 7) {
            courseUrlSegment = courseid_prefix + courseUrlSegment;
          }
          setConnectedCourseIds(prev =>
            prev.filter(id => id !== courseUrlSegment)
          );
        }
      }

      setCourses(prev => prev.map((course) =>
        course.id === courseId
          ? {
            ...course,
            connectionState: 'idle',
            UrlError: error instanceof Error
              ? error.message
              : 'Failed to connect. Please try again.'
          }
          : course
      ));
    }
  };

  const setActiveTab = (courseId: number, tab: 'CONNECT' | 'PREVIEW') => {
    setCourses(prev => prev.map(course =>
      course.id === courseId ? { ...course, activeTab: tab } : course
    ));
  };

  // Event handlers
  const handleClose = () => {
    const hasConnectedCourses = courses?.some(w => w.connectionState === "connected");
    const hasUnsavedChanges = !pipelineData || pipelineData.length === 0 || pendingSave;

    if (hasConnectedCourses && hasUnsavedChanges) {
      showConfirmationModal("Your changes have not been saved. Are you sure you want to close?", "Save Confirmation", async () => {
        setCustomModal((prev) => ({
          ...prev,
          isOpen: false,
        }));
        setCanvasCourseModalOpen(false);
      });
    } else {
      setCanvasCourseModalOpen(false);
    }
  };

  const handleGetDataNow = async (type: 'azure' | 'azure') => {
    setGettingData(true);
    setPendingSave(false);
    setGetDataClickTime(new Date());
    setCurrentStatus({ progress: 20, status: 'In Progress' });
    let queue_name = systemConfig.find(item => item.Id === 'canvas_queue_name')?.ConfigValue ?? '';
    if (type === 'azure') {
      queue_name = systemConfig.find(item => item.Id === 'canvas_queue_name')?.ConfigValue ?? '';
    }
    const run_type = 'Adhoc';
    const full_load_value = String(isCanvasFullLoad) === 'true' ? 'false' : 'true';
    if (queue_name) {
      const res = await triggerPiepeline(assistant.app_id, run_type, queue_name, full_load_value);
      setTimeout(() => {
        fetchPipelineData();
      }, 30000);
    }
  };

  const handleCancelPipeline = async () => {
  try {
    setCancellingPipeline(true);
    setCurrentStatus(prev => ({ ...prev, progress: 20, status: 'Cancelling' }));

    await cancelPipeline("canvas_to_vectordb", assistant.app_id);

    // Poll for status update after cancel request
    setTimeout(() => {
      fetchPipelineData();
    }, 2000);
  } catch (error) {
    console.error('Failed to cancel pipeline:', error);
    setCancellingPipeline(false);
    setCurrentStatus(prev => ({ ...prev, progress: 20, status: 'In Progress' }));
    
    setCustomModal({
      isOpen: true,
      title: "Cancel Failed",
      message: "Failed to cancel the operation. Please try again.",
      type: "error",
    });
  }
};

  const handleRefresh = async () => {
    showConfirmationModal("This action will fetch the canvas data again, and it might take a few minutes. Do you want to proceed?", "Refresh Preview Confirmation", async () => {
      // Close confirmation modal
      setCustomModal((prev) => ({
        ...prev,
        isOpen: false,
      }));

      if (isRefreshing) return;
      setIsRefreshing(true);

      // Show loading modal
      setCustomModal({
        isOpen: true,
        title: "Refreshing Canvas Data",
        message: "Please wait while we fetch the latest canvas data. This may take a few minutes...",
        type: "loading",
      });

      if (selectedCourse) {
        try {
          await fetchCourseItems(selectedCourse, true);

          // Show success modal when refresh completes
          setCustomModal({
            isOpen: true,
            title: "Refresh Completed",
            message: "Canvas data has been successfully refreshed with the latest information.",
            type: "success",
          });
        } catch (error) {
          console.error('Refresh failed:', error);

          // Show error modal if refresh fails
          setCustomModal({
            isOpen: true,
            title: "Refresh Failed",
            message: "Failed to refresh canvas data. Please try again later.",
            type: "error",
          });
        } finally {
          setIsRefreshing(false);
        }
      }
    });
  };

  const toggleExpandRow = (idx: number) => {
    setExpandedRows(prev => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const handleCheckboxChange = (itemId: string, itemType: string, courseId: number, idField: string) => {
  setCourses(prev =>
    prev.map(c =>
      c.id === courseId
        ? {
          ...c,
          courseItems: c.courseItems
            ? {
              ...c.courseItems,
              [itemType]: (c.courseItems as any)[itemType]?.map((item: any) => {
                const fieldValue = (item as any)[idField];
                return fieldValue === itemId
                  ? { ...item, checked: !item.checked }
                  : item;
              }),
            }
            : {},
        }
        : c
    )
  );

  setCheckedItems(prevCheckedItems =>
    prevCheckedItems.includes(itemId)
      ? prevCheckedItems.filter(id => id !== itemId)
      : [...prevCheckedItems, itemId]
  );
  triggerAutoSave();
};

  const selectedCourseData = courses.find(c => c.id === selectedCourse);
  const KaiBlobLocation = systemConfig.find(item => item.Id === 'blob_root_folder')?.ConfigValue;

  // Scroll to the expanded section when expandedGetData becomes true
  useEffect(() => {
    if (expandedGetData && expandedSectionRef.current) {
      expandedSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [expandedGetData]);

  return (
    <div
      className={`fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-60 ${isCanvasCourseModalOpen ? 'block' : 'hidden'
        }`}
      onClick={() => setCanvasCourseModalOpen(false)}
    >
      <div
        className="bg-white w-[90%] h-[80%] shadow-lg rounded-lg flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <CanvasHeader
          systemConfig={systemConfig}
          onClose={handleClose}
        />

        <div className="overflow-auto mt-[2%] h-full">
          <div className="flex flex-1 min-h-[85%]">
            <CoursesSidebar
              courses={courses}
              setCourses={setCourses}
              selectedCourse={selectedCourse}
              setSelectedCourse={setSelectedCourse}
              addNewCourse={addNewCourse}
              systemConfig={systemConfig}
              showDeleteConfirmationModal={showDeleteConfirmationModal}
              connectedCourseIds={connectedCourseIds}
              setConnectedCourseIds={setConnectedCourseIds}
              currentStatus={currentStatus}
              triggerAutoSave={triggerAutoSave}
              fetchCalledRef={fetchCalledRef}
              isCanvasFullLoad={isCanvasFullLoad}
              triggerPiepeline={triggerPiepeline}
              assistant={assistant}
              setPendingSave={setPendingSave}
              fetchPipelineData={fetchPipelineData}
              setCustomModal={setCustomModal}
            />

            <div className="flex flex-col h-full w-full p-2">
              <CourseTabNavigation
                selectedCourseData={selectedCourseData}
                setActiveTab={setActiveTab}
                selectedCourse={selectedCourse}
                showDocumentCache={showDocumentCache}
                handleRefresh={handleRefresh}
                isRefreshing={isRefreshing}
              />

              <div className="flex-1 overflow-y-auto" ref={expandedSectionRef}>
                {selectedCourseData?.activeTab === 'CONNECT' && (
                  <ConnectTab
                    selectedCourseData={selectedCourseData}
                    systemConfig={systemConfig}
                    handleConnect={handleConnect}
                    selectedCourse={selectedCourse}
                    handleUrlChange={(value: string) => {
                      setCourses(prev => prev.map(course =>
                        course.id === selectedCourse ? { ...course, url: value } : course
                      ));
                    }}
                    setShowHowImage={setShowHowImage}
                    setImageSrc={setImageSrc}
                    setModalSize={setModalSize}
                    KaiBlobLocation={KaiBlobLocation}
                    safeAiIconSrc={safeAiIconSrc}
                  />
                )}

                {selectedCourseData?.activeTab === 'PREVIEW' && (
                  <PreviewTab
                    selectedCourseData={selectedCourseData}
                    isLoadingTabs={isLoadingTabs}
                    handleToggleChange={(key: VisibilityKey, courseId: number) => {
                      handleToggleChange(key, courseId);
                      setPendingSave(true);
                    }}
                    handleCheckboxChange={handleCheckboxChange}
                    checkedItems={checkedItems}
                    updateExcludedItems={updateExcludedItems}
                  />
                )}
              </div>
            </div>
          </div>

        <CanvasFooter
  isCanvasRNightly={isCanvasRNightly}
  setIsCanvasRNightly={setIsCanvasRNightly}
  triggerAutoSave={triggerAutoSave}
  setModalRefreshNModal={setModalRefreshNModal}
  assistant={assistant}
  currentStatus={currentStatus}
  courses={courses}
  gettingData={gettingData}
  pendingSave={pendingSave}
  lastSaveTarget={lastSaveTarget}
  handleGetDataNow={handleGetDataNow}
  expandedGetData={expandedGetData}
  setExpandedGetData={setExpandedGetData}
  pipelineData={pipelineData}
  expandedRows={expandedRows}
  toggleExpandRow={toggleExpandRow}
  // ADD THESE TWO NEW PROPS
  onCancelPipeline={handleCancelPipeline}
  cancellingPipeline={cancellingPipeline}
/>
        </div>
      </div>

      <CanvasModals
        customModal={customModal}
        setCustomModal={setCustomModal}
        modalRefreshNModal={modalRefreshNModal}
        setModalRefreshNModal={setModalRefreshNModal}
        showHowImage={showHowImage}
        setShowHowImage={setShowHowImage}
        modalSize={modalSize}
        imageSrc={imageSrc}
      />
    </div>
  );
};

export default CanvasCourse;